import { useContext, useMemo, useState } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import {
  Box,
  Alert,
  Button,
  Dialog,
  TextField,
  Typography,
  DialogActions,
  DialogContent
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import useContactWrites from "hooks/contacts/useContactWrites";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { isStrictValidPhone } from "utils/fieldValidators";
import {
  contactRekeyCacheKeys,
  describeChangePhoneFailure,
  formatPortedSummary
} from "utils/contacts/contactRekey";
import type { ChangePhoneFailure } from "utils/contacts/contactRekey";
import { invalidateCached } from "../detail/data/sessionCache";
import { CONTACT_DETAIL_VIEW_PARAM, readContactDetailViewParam } from "../detail/ContactDetailView";
import type { REST_schema } from "../../../../@types/sharedSchema";

/**
 * Phone-number change (עדכון מספר) — the UI over `POST /api/v1/contacts/change-phone`.
 *
 * A contact's Firestore doc id IS its international phone, so this is not a field edit:
 * the server RE-KEYS the document and ports everything attached to it (notes, calendar,
 * signature documents, calls, minisite favorites, …), then hard-deletes the old doc.
 * Irreversible, which is why the copy says so before the input.
 *
 * ── The three browser-side repairs, run RIGHT AFTER the server succeeds ────────────
 *
 *  1. The in-memory contacts — the legacy book's row moves to the new id, so the old
 *     contacts table repaints without a refetch, and the lookup store drops the old
 *     number. Done by the write itself (`useContactWrites().changeContactPhone`).
 *  2. The `?contact=` URL param — the in-place detail view (/dashboard/contacts) and
 *     the CRM side panel are both KEYED on that param; left pointing at the old id
 *     they would resolve to "לא נמצא" the moment the live listener sees the delete.
 *     MERGED with `navigator.replace`, never rebuilt — filters and dialog params on
 *     the same URL must survive (the `useOpenContactDetail` rule).
 *  3. The customer-page session caches for the old id are invalidated — a cache entry
 *     describing a deleted document is worse than a cold one.
 *
 * They run BEFORE the success summary is shown (not on dismiss) so the repairs land
 * even if the surrounding dialogs unmount under us — on the CRM page the live listener
 * on the old id fires `missing` as soon as the server deletes it.
 *
 * ── Error contract (see `WebotServerContext.contactsChangePhone`) ──────────────────
 *
 * Decided in `describeChangePhoneFailure` (utils/contacts/contactRekey) — pure, and tested
 * without mounting this dialog:
 *
 *  - 400 `CRM_INVALID_REQUEST` / 409 `CRM_CONFLICT` → the server's own message is
 *    rendered VERBATIM: the 409 text distinguishes a live collision from a
 *    soft-deleted twin, and no client copy can reproduce that distinction.
 *  - 403 → nothing moved, and it never will for this user: in an office's shared book a
 *    number is changed by whoever has the customer on their list, or by full access.
 *  - 503 `CRM_BOOK_BUSY` → nothing moved; the office's switch is running. The server's
 *    sentence, "נסה שוב", and the number stays editable.
 *  - `CRM_REKEY_PARTIAL`, any other 5xx, the network → the port may be PARTIAL. Retrying
 *    the SAME request is safe (the server resumes), so the dialog locks the input and
 *    offers "נסה שוב" — changing the target mid-resume is exactly what the lock prevents.
 *
 * Hebrew copy is hardcoded (the CRM-surface convention); phone numbers render LTR
 * inside the RTL copy via `dir="ltr"` spans.
 */

type ChangePhoneDialogProps = {
  open: boolean;
  /** The contact's CURRENT doc id — its international (E.164) phone number. */
  contactId: string;
  /** Display name for the header line; the number stands in when absent. */
  contactName?: string;
  /** User-initiated close BEFORE anything changed (backdrop / ביטול). */
  onClose: () => void;
  /**
   * The user dismissed the SUCCESS state — the change already happened and the
   * in-memory/URL/cache repairs already ran. Hosts close themselves here (the edit
   * form above is keyed to the old, now-deleted doc id) and fix any state THEY own.
   */
  onCompleted: (result: REST_schema.ChangeContactPhoneResponse) => void;
  /** Stacking override for hosts that already float above 1300 (see EditContactDialog). */
  zIndex?: number;
};

/** LTR phone inside RTL copy. `dir` alone isolates; the weight makes it scannable. */
function PhoneSpan({ phone }: { phone: string }): JSX.Element {
  return (
    <Box component="span" dir="ltr" sx={{ fontWeight: 700, unicodeBidi: "isolate" }}>
      {phone}
    </Box>
  );
}

export function ChangePhoneDialog({
  open,
  contactId,
  contactName,
  onClose,
  onCompleted,
  zIndex
}: ChangePhoneDialogProps): JSX.Element {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { changeContactPhone } = useContactWrites();

  const [newPhone, setNewPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ChangePhoneFailure | null>(null);
  const [result, setResult] = useState<REST_schema.ChangeContactPhoneResponse | null>(null);

  // Same parsing + validation the CREATE flow applies to a typed primary phone
  // (EditContactDialog / ContactDialogInputPhoneNumber): lenient parse for the E.164
  // preview, strict shape check for the gate.
  const trimmed = newPhone.trim();
  const parsed = useMemo(() => (trimmed ? parsePhoneNumber(trimmed) : null), [trimmed]);
  const normalized = (parsed?.number as string | undefined) ?? null;
  const phoneInvalid = !!trimmed && (!isStrictValidPhone(trimmed) || !normalized);
  const samePhone = !!normalized && normalized === contactId;
  // Locked only while a change may be HALF DONE: a refusal that moved nothing (an office whose
  // switch is running, say) leaves the number editable even though the action reads "נסה שוב".
  const inputBlocked = submitting || !!result || !!serverError?.partial;

  const submit = async (): Promise<void> => {
    if (submitting || result) return;
    if (!trimmed || phoneInvalid || samePhone) return;
    setSubmitting(true);
    setServerError(null);
    try {
      // The typed value goes as-is — the server normalizes any parseable format and
      // answers with the E.164 id it actually stored (which is the one that matters).
      // Repair 1 — the in-memory contacts (the legacy book and the lookup store) — is part
      // of the write itself: `useContactWrites().changeContactPhone`.
      const response = await changeContactPhone({ contactId, newPhone: trimmed });

      // Repair 2: the ?contact= param, when it points at the old id. MERGE, never
      // rebuild; `replace` so Back does not step through a dead id.
      const params = new URLSearchParams(window.location.search);
      if (readContactDetailViewParam(params) === contactId) {
        params.set(CONTACT_DETAIL_VIEW_PARAM, response.newContactId);
        const search = params.toString();
        navigator.replace({ pathname: window.location.pathname, search: search ? `?${search}` : "" });
      }
      // Repair 3: session caches keyed to the old id.
      contactRekeyCacheKeys(contactId).forEach((key) => invalidateCached(key));

      setResult(response);
    } catch (error) {
      console.error("Contact phone change failed", error);
      setServerError(describeChangePhoneFailure(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Backdrop / Escape: blocked mid-request; after success it means "done", same as the
  // button — the change happened, so a plain onClose would leave the host's stale form open.
  const handleDialogClose = (): void => {
    if (submitting) return;
    if (result) onCompleted(result);
    else onClose();
  };

  const summaryLine = result ? formatPortedSummary(result.portedSummary) : "";

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      onClose={handleDialogClose}
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      {result ? (
        <>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 1 }}>המספר עודכן ✓</Typography>
            <Typography variant="body2" sx={{ mb: summaryLine ? 1 : 0 }}>
              {contactName || "הלקוח"} הועבר למספר <PhoneSpan phone={result.newContactId} />.
            </Typography>
            {summaryLine && (
              <Typography variant="body2" color="text.secondary">
                {summaryLine}
              </Typography>
            )}
            {!result.mirrorSynced && (
              // The Postgres mirror drains within ~a minute; until then the CRM list
              // may still show the old row. Saying so beats looking broken.
              <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1.5 }}>
                רשימת הלקוחות עשויה להציג את המספר הישן עוד כדקה, עד שהיא מתעדכנת.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={() => onCompleted(result)} sx={{ fontWeight: 700 }}>
              סגירה
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent sx={{ pt: 3 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>
              עדכון מספר טלפון
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {contactName ? `${contactName} · ` : ""}
              המספר הנוכחי: <PhoneSpan phone={contactId} />
            </Typography>

            <Typography variant="body2" sx={{ mb: 2 }}>
              הלקוח וכל מה שמקושר אליו — הערות, פגישות ומשימות, מסמכי חתימה, שיחות,
              נכסים במיניסייט ועוד — יעברו למספר החדש. קישורים ישנים שמבוססים על המספר
              הקודם יפסיקו לעבוד, ולא ניתן לבטל את הפעולה.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              type="tel"
              label="המספר החדש"
              value={newPhone}
              disabled={inputBlocked}
              error={phoneInvalid || samePhone}
              helperText={
                phoneInvalid
                  ? "מספר הטלפון אינו תקין"
                  : samePhone
                  ? "זה המספר הנוכחי של הלקוח"
                  : ""
              }
              inputProps={{ dir: "ltr" }}
              onChange={(event) => setNewPhone(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void submit();
                }
              }}
            />
            {normalized && !phoneInvalid && !samePhone && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                יישמר בפורמט בינלאומי: <PhoneSpan phone={normalized} />
              </Typography>
            )}

            {serverError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {serverError.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              color="inherit"
              disabled={submitting}
              onClick={onClose}
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              ביטול
            </Button>
            <Box sx={{ flex: 1 }} />
            <LoadingButton
              variant="contained"
              loading={submitting}
              disabled={!trimmed || phoneInvalid || samePhone}
              onClick={() => void submit()}
              sx={{ fontWeight: 700 }}
            >
              {serverError?.retryable ? "נסה שוב" : "עדכון המספר"}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
